import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import client from '../../api/client';
import { getFeedbackAssetBlob } from '../../api/feedback';
import { redownloadDienstzeitProtokollPdf } from '../../components/pdf/dienstzeitPdfExport';
import { useNotifications } from '../../contexts/NotificationContext';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('de-DE');
}

function parseMeta(metaJson) {
  if (!metaJson) return null;
  if (typeof metaJson === 'object') return metaJson;
  try {
    return JSON.parse(metaJson);
  } catch {
    return null;
  }
}

function getNotificationTarget(item) {
  const meta = parseMeta(item.meta_json);
  if (!meta) return null;

  if (item.type === 'monthly-backup-reminder') {
    return {
      label: 'Datensicherung öffnen',
      path: '/benutzer?backup=open',
    };
  }

  if (meta.feedbackEntryId) {
    const topicQuery = meta.topicKey ? `type=${encodeURIComponent(meta.topicKey)}&` : '';
    return {
      label: 'Zur Rückmeldung',
      path: `/rueckmeldung?${topicQuery}entry=${encodeURIComponent(meta.feedbackEntryId)}`,
    };
  }

  if (meta.entryId) {
    return {
      label: 'Zum Eintrag',
      path: `/logbuch?entry=${meta.entryId}`,
    };
  }

  if (meta.requestId) {
    return {
      label: 'Zur Anfrage',
      path: `/logbuch?request=${meta.requestId}`,
    };
  }

  return null;
}

function getPdfExportId(item) {
  const meta = parseMeta(item.meta_json);
  const exportId = Number(meta?.pdfExportId);
  return Number.isFinite(exportId) ? exportId : null;
}

function getPublicReturnUrl(item) {
  const meta = parseMeta(item.meta_json);
  return meta?.publicReturnUrl || '';
}

function getPublishedContent(item) {
  const meta = parseMeta(item.meta_json);
  return meta?.publishedContent || null;
}

function BenachrichtigungenPage() {
  const { markAllReadLocally, markOneReadLocally, refresh: refreshUnreadCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [assetPreviewUrls, setAssetPreviewUrls] = useState({});
  const [loadingAssetKey, setLoadingAssetKey] = useState('');
  const navigate = useNavigate();

  const loadNotifications = async () => {
    try {
      setError('');
      const response = await client.get('/api/notifications');
      setNotifications(response.data.notifications || []);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Benachrichtigungen konnten nicht geladen werden.');
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const markRead = async (id) => {
    const notification = notifications.find((item) => item.id === id);
    if (!notification || notification.is_read) {
      return;
    }

    try {
      await client.post(`/api/notifications/${id}/read`);
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: 1 } : item)));
      markOneReadLocally();
    } catch (markError) {
      await refreshUnreadCount();
      setError(markError.response?.data?.error || 'Benachrichtigung konnte nicht aktualisiert werden.');
    }
  };

  const markAllRead = async () => {
    const hasUnreadNotifications = notifications.some((item) => !item.is_read);
    if (!hasUnreadNotifications) {
      return;
    }

    try {
      await client.post('/api/notifications/read-all');
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: 1 })));
      markAllReadLocally();
    } catch (markError) {
      await refreshUnreadCount();
      setError(markError.response?.data?.error || 'Benachrichtigungen konnten nicht aktualisiert werden.');
    }
  };

  const handleOpenTarget = async (item, target) => {
    try {
      if (!item.is_read) {
        await markRead(item.id);
      }
      navigate(target.path);
    } catch {
      navigate(target.path);
    }
  };

  const handleRedownloadPdf = async (item) => {
    const exportId = getPdfExportId(item);
    if (!exportId) {
      setError('Zu dieser Benachrichtigung ist keine PDF hinterlegt.');
      return;
    }

    try {
      setError('');
      setInfo('');
      await redownloadDienstzeitProtokollPdf(exportId);
      setInfo(`Stundenprotokoll #${exportId} wurde erneut heruntergeladen.`);
      if (!item.is_read) {
        await markRead(item.id);
      }
    } catch (downloadError) {
      setError(downloadError.response?.data?.error || 'PDF konnte nicht heruntergeladen werden.');
    }
  };

  const handleCopyPublicLink = async (item) => {
    const url = getPublicReturnUrl(item);
    if (!url) {
      setError('Kein externer Rückläufer-Link verfügbar.');
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setInfo('Externer Rückläufer-Link wurde in die Zwischenablage kopiert.');
      if (!item.is_read) {
        await markRead(item.id);
      }
    } catch {
      setError('Link konnte nicht in die Zwischenablage kopiert werden.');
    }
  };

  const unread = notifications.filter((item) => !item.is_read).length;

  const handlePreviewPublishedAsset = async (entryId, asset) => {
    if (!entryId || !asset) {
      return;
    }

    const assetKey = `${entryId}:${asset.id}`;
    if (asset.asset_kind !== 'document' && assetPreviewUrls[assetKey]) {
      return;
    }

    try {
      setLoadingAssetKey(assetKey);
      const response = await getFeedbackAssetBlob(entryId, asset.id, false);
      const objectUrl = URL.createObjectURL(response.data);

      if (asset.asset_kind === 'document') {
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } else {
        setAssetPreviewUrls((prev) => ({ ...prev, [assetKey]: objectUrl }));
      }
    } catch (assetError) {
      setError(assetError.response?.data?.error || 'Anhang konnte nicht geladen werden.');
    } finally {
      setLoadingAssetKey('');
    }
  };

  const handleDownloadPublishedAsset = async (entryId, asset) => {
    if (!entryId || !asset) {
      return;
    }

    const assetKey = `${entryId}:${asset.id}`;

    try {
      setLoadingAssetKey(assetKey);
      const response = await getFeedbackAssetBlob(entryId, asset.id, true);
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = asset.original_name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (assetError) {
      setError(assetError.response?.data?.error || 'Anhang konnte nicht heruntergeladen werden.');
    } finally {
      setLoadingAssetKey('');
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 5 }, py: 2.5 }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.75 }}>
              Benachrichtigungen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aenderungen, Freigaben und Rueckmeldungen an einer Stelle.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={markAllRead} disabled={!unread}>
            Alle als gelesen
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {info && <Alert severity="success">{info}</Alert>}

        {!notifications.length && (
          <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Keine Benachrichtigungen vorhanden.
              </Typography>
            </CardContent>
          </Card>
        )}

        {notifications.map((item) => (
          <Card key={item.id} sx={{ borderRadius: 3, border: 1, borderColor: item.is_read ? 'divider' : 'primary.main' }}>
            <CardContent>
              <Stack spacing={1}>
                {(() => {
                  const target = getNotificationTarget(item);
                  const publishedContent = getPublishedContent(item);
                  return (
                    <>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {item.title}
                        </Typography>
                        {!item.is_read && <Chip label="Neu" size="small" color="primary" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {item.message || 'Neue Benachrichtigung'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(item.created_at)}
                      </Typography>
                      {publishedContent && (
                        <Stack spacing={1} sx={{ pt: 0.5 }}>
                          <Typography variant="body2">
                            {publishedContent.description}
                          </Typography>

                          {Array.isArray(publishedContent.links) && publishedContent.links.length > 0 && (
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                URLs
                              </Typography>
                              {publishedContent.links.map((link) => (
                                <Typography key={link} variant="body2">
                                  <a href={link} target="_blank" rel="noreferrer">{link}</a>
                                </Typography>
                              ))}
                            </Stack>
                          )}

                          {publishedContent.resolution?.needsFollowUp === 'yes' && (
                            <Typography variant="body2">
                              Aufarbeitung: {publishedContent.resolution.followUpSummary || '-'}
                            </Typography>
                          )}
                          {publishedContent.resolution?.needsFollowUp === 'no' && (
                            <Typography variant="body2">
                              Keine Aufarbeitung erforderlich: {publishedContent.resolution.noFollowUpReason || '-'}
                            </Typography>
                          )}

                          {[
                            ...(publishedContent.images || []),
                            ...(publishedContent.videos || []),
                            ...(publishedContent.followUpPdf ? [publishedContent.followUpPdf] : []),
                          ].map((asset) => {
                            const assetKey = `${publishedContent.entryId}:${asset.id}`;
                            return (
                              <Stack key={assetKey} spacing={0.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                  <Typography variant="caption" color="text.secondary">
                                    {asset.original_name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={loadingAssetKey === assetKey}
                                      onClick={() => handlePreviewPublishedAsset(publishedContent.entryId, asset)}
                                    >
                                      {loadingAssetKey === assetKey ? 'Lädt...' : (asset.asset_kind === 'document' ? 'PDF öffnen' : 'Ansehen')}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      disabled={loadingAssetKey === assetKey}
                                      onClick={() => handleDownloadPublishedAsset(publishedContent.entryId, asset)}
                                    >
                                      Herunterladen
                                    </Button>
                                  </Box>
                                </Stack>
                                {assetPreviewUrls[assetKey] && asset.asset_kind === 'image' && (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      borderRadius: 2,
                                      border: 1,
                                      borderColor: 'divider',
                                      bgcolor: 'grey.50',
                                      overflow: 'hidden',
                                      p: { xs: 0.5, sm: 1 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={assetPreviewUrls[assetKey]}
                                      alt={asset.original_name}
                                      sx={{
                                        display: 'block',
                                        width: '100%',
                                        maxWidth: '100%',
                                        height: 'auto',
                                        maxHeight: { xs: 220, sm: 260, md: 320 },
                                        objectFit: 'contain',
                                        borderRadius: 1.5,
                                      }}
                                    />
                                  </Box>
                                )}
                                {assetPreviewUrls[assetKey] && asset.asset_kind === 'video' && (
                                  <Box component="video" controls src={assetPreviewUrls[assetKey]} sx={{ width: '100%', maxHeight: 320, borderRadius: 2 }} />
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {!item.is_read && (
                          <Button size="small" variant="text" onClick={() => markRead(item.id)}>
                            Als gelesen markieren
                          </Button>
                        )}
                        {target && (
                          <Button size="small" variant="outlined" onClick={() => handleOpenTarget(item, target)}>
                            {target.label}
                          </Button>
                        )}
                        {getPdfExportId(item) && (
                          <Button size="small" variant="outlined" onClick={() => handleRedownloadPdf(item)}>
                            PDF erneut herunterladen
                          </Button>
                        )}
                        {getPublicReturnUrl(item) && (
                          <Button size="small" variant="text" onClick={() => handleCopyPublicLink(item)}>
                            Ruecklaeufer-Link kopieren
                          </Button>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

export default BenachrichtigungenPage;
